/**
 * Category and tag selector component for essay organization.
 * 
 * Features:
 * - Category dropdown (single-select)
 * - Tags multi-select with autocomplete
 * - Create new category/tag inline
 * - Display selected tags as removable chips
 * - Save changes on selection
 * 
 * Called by:
 * - EssayDetailPage for managing essay organization
 */
import { useState } from 'react';
import {
  Select,
  Tag as AntTag,
  Space,
  Button,
  Input,
  message,
  Typography,
  Divider,
} from 'antd';
import { PlusOutlined, FolderOutlined, TagsOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCategories,
  createCategory,
  setEssayCategory,
  getTags,
  createTag,
  setEssayTags,
} from '../services/api';
import type { Category, Tag, User } from '@compendium/shared';

const { Text } = Typography;

// =============================================================================
// Props Interface
// =============================================================================

interface CategoryTagSelectorProps {
  /** Essay slug for API calls */
  slug: string;
  /** Currently selected category ID (or null) */
  currentCategoryId: number | null;
  /** Currently selected tag IDs */
  currentTagIds: number[];
  /** Current user for permission checks */
  user: User;
  /** Callback when category changes */
  onCategoryChange?: (category: Category | null) => void;
  /** Callback when tags change */
  onTagsChange?: (tags: Tag[]) => void;
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Category and tag selector component.
 * Allows selecting category (single) and tags (multiple) for essays.
 */
function CategoryTagSelector({
  slug,
  currentCategoryId,
  currentTagIds,
  user,
  onCategoryChange,
  onTagsChange,
}: CategoryTagSelectorProps) {
  const queryClient = useQueryClient();
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const isAuthor = user.role === 'author';

  // Fetch categories
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
  });

  // Fetch tags
  const { data: tags = [], isLoading: tagsLoading } = useQuery({
    queryKey: ['tags'],
    queryFn: getTags,
  });

  // Create category mutation
  const createCategoryMutation = useMutation({
    mutationFn: (name: string) => createCategory({ name }),
    onSuccess: (newCategory) => {
      message.success(`Category "${newCategory.name}" created`);
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setNewCategoryName('');
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to create category');
    },
  });

  // Set category mutation
  const setCategoryMutation = useMutation({
    mutationFn: (categoryId: number | null) => setEssayCategory(slug, categoryId),
    onSuccess: (category) => {
      message.success(category ? `Category set to "${category.name}"` : 'Category removed');
      onCategoryChange?.(category);
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to set category');
    },
  });

  // Create tag mutation
  const createTagMutation = useMutation({
    mutationFn: (name: string) => createTag({ name }),
    onSuccess: (newTag) => {
      message.success(`Tag "${newTag.name}" created`);
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setNewTagName('');
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to create tag');
    },
  });

  // Set tags mutation
  const setTagsMutation = useMutation({
    mutationFn: (tagIds: number[]) => setEssayTags(slug, tagIds),
    onSuccess: (updatedTags) => {
      message.success('Tags updated');
      onTagsChange?.(updatedTags);
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to set tags');
    },
  });

  /**
   * Handle category selection change.
   */
  function handleCategoryChange(categoryId: number | undefined) {
    if (!isAuthor) return;
    setCategoryMutation.mutate(categoryId || null);
  }

  /**
   * Handle tags selection change.
   */
  function handleTagsChange(tagIds: number[]) {
    if (!isAuthor) return;
    setTagsMutation.mutate(tagIds);
  }

  /**
   * Handle creating a new category.
   */
  function handleCreateCategory() {
    if (!newCategoryName.trim()) return;
    createCategoryMutation.mutate(newCategoryName.trim());
  }

  /**
   * Handle creating a new tag.
   */
  function handleCreateTag() {
    if (!newTagName.trim()) return;
    createTagMutation.mutate(newTagName.trim());
  }

  // Category options for Select
  const categoryOptions = categories.map((cat) => ({
    value: cat.id,
    label: cat.name,
  }));

  // Tag options for Select
  const tagOptions = tags.map((tag) => ({
    value: tag.id,
    label: tag.name,
  }));

  // Custom dropdown render for category with create option
  const categoryDropdownRender = (menu: React.ReactNode) => (
    <>
      {menu}
      {isAuthor && (
        <>
          <Divider style={{ margin: '8px 0' }} />
          <Space style={{ padding: '0 8px 8px' }}>
            <Input
              placeholder="New category"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onPressEnter={handleCreateCategory}
              size="small"
              style={{ flex: 1 }}
            />
            <Button
              type="text"
              icon={<PlusOutlined />}
              onClick={handleCreateCategory}
              loading={createCategoryMutation.isPending}
              size="small"
            >
              Add
            </Button>
          </Space>
        </>
      )}
    </>
  );

  // Custom dropdown render for tags with create option
  const tagDropdownRender = (menu: React.ReactNode) => (
    <>
      {menu}
      {isAuthor && (
        <>
          <Divider style={{ margin: '8px 0' }} />
          <Space style={{ padding: '0 8px 8px' }}>
            <Input
              placeholder="New tag"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onPressEnter={handleCreateTag}
              size="small"
              style={{ flex: 1 }}
            />
            <Button
              type="text"
              icon={<PlusOutlined />}
              onClick={handleCreateTag}
              loading={createTagMutation.isPending}
              size="small"
            >
              Add
            </Button>
          </Space>
        </>
      )}
    </>
  );

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Category selector */}
      <div style={{ marginBottom: 20 }}>
        <Space style={{ marginBottom: 8 }}>
          <FolderOutlined />
          <Text strong>Category</Text>
        </Space>
        <Select
          placeholder="Select category"
          allowClear
          value={currentCategoryId || undefined}
          onChange={handleCategoryChange}
          options={categoryOptions}
          loading={categoriesLoading}
          disabled={!isAuthor || setCategoryMutation.isPending}
          style={{ width: '100%' }}
          dropdownRender={categoryDropdownRender}
        />
        {!isAuthor && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
            Only the author can change the category
          </Text>
        )}
      </div>

      {/* Tags selector */}
      <div>
        <Space style={{ marginBottom: 8 }}>
          <TagsOutlined />
          <Text strong>Tags</Text>
        </Space>
        <Select
          mode="multiple"
          placeholder="Select tags"
          value={currentTagIds}
          onChange={handleTagsChange}
          options={tagOptions}
          loading={tagsLoading}
          disabled={!isAuthor || setTagsMutation.isPending}
          style={{ width: '100%' }}
          dropdownRender={tagDropdownRender}
          tagRender={(props) => {
            const { label, closable, onClose } = props;
            return (
              <AntTag
                closable={closable && isAuthor}
                onClose={onClose}
                style={{ marginRight: 3 }}
              >
                {label}
              </AntTag>
            );
          }}
        />
        {!isAuthor && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
            Only the author can change tags
          </Text>
        )}
      </div>
    </div>
  );
}

export default CategoryTagSelector;
